import { useState } from "react";

import { makePostApiCall, makeGetApiCall } from "../../shared/makeApiCall";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import FilterSingleSelectDropdown from "../../components/filterDropdown/FilterSingleSelectDropdown";
import { generateUniqueNumber, orderStatusOptions } from "../../shared/utility";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import ProgressModal from "../../components/modals/ProgressModal";
import subDays from "date-fns/subDays";

export function ManageOrdersViewModel(logout, addSnackbar) {
  const [customers, setCustomers] = useState([]);

  const defaultFilters = {
    customer: { value: -1, label: "All Customers" },
    orderStatus: { value: -1, label: "All Orders" },
    startDate: subDays(new Date(), 7),
    endDate: new Date(),
  };

  const [filters, setFilters] = useState(defaultFilters);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState({ data: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [analytics, setAnalytics] = useState({
    range: null,
    stats: [],
    loading: true,
  });
  const [showDates, setShowDates] = useState(false);

  const showProgressModal = (message = "Processing...") => {
    setEmptyModelOpen({
      childComponent: <ProgressModal message={message} />,
      open: true,
    });
  };

  const hideProgressModal = () => {
    setEmptyModelOpen({
      childComponent: null,
      open: false,
    });
  };

  const getOrders = async function (search, page, filterArg) {
    setLoading(true);
    const payload = {
      page: page,
      filters: { ...filterArg, search: search },
    };
    let callResult = await makePostApiCall("fetchOrders", payload);

    if (callResult.success) {
      setOrders(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setLoading(false);
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const deleteOrder = async function (id) {
    let callResult = await makePostApiCall(`deleteOrder/${id}`);

    if (callResult.success) {
      const filteredItems = orders.data.filter((order) => order.id !== id);
      setOrders({ ...orders, data: filteredItems });

      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const askDeleteOrder = function (order) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteOrder(order.id)}
          message={`Are you sure you want to delete ${order.po_number}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const getCustomersForDropdown = async function () {
    let callResult = await makePostApiCall(`getCustomersForDropdown`);
    if (callResult.success) {
      setCustomers(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const resetFilters = () => {
    setFilters(defaultFilters);

    setShowFilters(false);
    getOrders(null, 1, defaultFilters);
  };

  const filterComponents = [
    <FilterSingleSelectDropdown
      header="Order Status"
      onChange={(value) =>
        setFilters({
          ...filters,
          orderStatus: value,
        })
      }
      options={orderStatusOptions}
      resetFilter={() =>
        setFilters({
          ...filters,
          orderStatus: { value: -1, label: "All Orders" },
        })
      }
      valueName="orderStatus"
      value={filters.orderStatus}
    />,
    <FilterSingleSelectDropdown
      header="Customer"
      onChange={(value) =>
        setFilters({
          ...filters,
          customer: value,
        })
      }
      options={customers}
      resetFilter={() =>
        setFilters({
          ...filters,
          customer: { value: -1, label: "All Customers" },
        })
      }
      valueName="customer"
      value={filters.customer}
    />,
  ];

  const fetchEstimates = async function () {
    showProgressModal("Fetching estimates...");
    let callResult = await makeGetApiCall(`quickbooks/fetchEstimates`);
    hideProgressModal();
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
      getOrders(null, 1, filters);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  return {
    customers,
    filterComponents,
    getCustomersForDropdown,
    getOrders,
    loading,
    orders,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    emptyModal,
    askDeleteOrder,
    filters,
    resetFilters,
    setFilters,
    setShowFilters,
    showFilters,
    fetchEstimates,
    analytics,
    showDates,
    setShowDates,
    setFilters,
  };
}
